import { type CellContext } from "@tanstack/react-table";

import { type DealTrackingLimitedPartnerBreakdown } from "@vega-api/generated/types/dealTrackingLimitedPartnerBreakdown";

export const TOTAL_ROW_ID = "__TOTAL__";

/**
 * Check if a row is the total row
 */
export const isTotalRow = (row: DealTrackingLimitedPartnerBreakdown): boolean =>
  row.limitedPartnerId === TOTAL_ROW_ID;

/**
 * Higher-order function to create cell renderers with total row support.
 * For total rows, displays the value in semibold styling.
 * For regular rows, calls the provided render function.
 */
export const createCellWithTotalSupport =
  <TValue,>(
    renderRegular: (
      row: DealTrackingLimitedPartnerBreakdown,
      value: TValue,
    ) => React.ReactNode,
    formatForTotal?: (
      row: DealTrackingLimitedPartnerBreakdown,
    ) => React.ReactNode,
  ) =>
  ({
    row,
    getValue,
  }: CellContext<DealTrackingLimitedPartnerBreakdown, TValue>) => {
    const value = getValue();

    if (isTotalRow(row.original)) {
      const displayValue = formatForTotal
        ? formatForTotal(row.original)
        : String(value);

      return (
        <span className="body-base-semibold text-text/primary">
          {displayValue}
        </span>
      );
    }

    return renderRegular(row.original, value);
  };

/**
 * Simple cell renderer that shows em-dash for total rows, regular text otherwise
 */
export const createSimpleTotalCell = () =>
  createCellWithTotalSupport<string>((_, value) => (
    <span className="body-base-medium text-text/primary">{value}</span>
  ));
